import React from "react";
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  TextField,
} from "@mui/material";
import { History } from "lucide-react";
import { useScrollytellTranslation } from "../Admin/scrollytellContext";
import { isSafeHistoricTileUrl } from "./mapWaypoints";
import { HISTORIC_PRESETS } from "./historicPresets";

export { HISTORIC_PRESETS };

export const HistoricMapOverlayControls = ({
  historicOverlay,
  onUpdateOverlay,
  waypoints = [],
  onUpdateWaypointOpacity,
}) => {
  const t = useScrollytellTranslation();
  const overlay = historicOverlay || { enabled: false, url: "", name: "" };
  const isKartverket = Boolean(overlay.url && overlay.url.includes("cache.kartverket.no"));

  const handleToggle = (enabled) => {
    onUpdateOverlay({
      ...overlay,
      enabled,
      url: overlay.url || HISTORIC_PRESETS[0].url,
      name: overlay.name || HISTORIC_PRESETS[0].name,
      filter: overlay.filter || HISTORIC_PRESETS[0].filter,
      attribution: overlay.attribution || HISTORIC_PRESETS[0].attribution,
    });
  };

  const handlePresetChange = (e) => {
    const selected = HISTORIC_PRESETS.find((p) => p.name === e.target.value);
    if (selected && selected.url) {
      onUpdateOverlay({
        ...overlay,
        url: selected.url,
        name: selected.name,
        filter: selected.filter || "none",
        attribution: selected.attribution,
      });
    } else {
      onUpdateOverlay({
        ...overlay,
        name: "custom",
        filter: "none",
      });
    }
  };

  const handleUrlChange = (e) => {
    onUpdateOverlay({
      ...overlay,
      url: e.target.value,
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <History size={16} color="#0284C7" />
        <Typography
          variant="caption"
          sx={{ color: "#64748B", fontWeight: 700, textTransform: "uppercase" }}
        >
          {t("historicOverlay.title", "Historisk kartlag ('Tidsmaskin')")}
        </Typography>
      </Box>

      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={Boolean(overlay.enabled)}
            onChange={(e) => handleToggle(e.target.checked)}
            inputProps={{
              "aria-label": t(
                "historicOverlay.toggle",
                "Aktiver historisk kartlag"
              ),
            }}
          />
        }
        label={
          <Typography sx={{ fontSize: "13px", color: "#1E293B" }}>
            {t("historicOverlay.enabledLabel", "Aktiver tidsmaskin-overlay")}
          </Typography>
        }
      />

      {overlay.enabled && (
        <>
          <FormControl fullWidth size="small">
            <InputLabel id="historic-preset-label" sx={{ color: "#475569" }}>
              {t("historicOverlay.presetLabel", "Historisk kart")}
            </InputLabel>
            <Select
              native
              labelId="historic-preset-label"
              label={t("historicOverlay.presetLabel", "Historisk kart")}
              value={overlay.name || HISTORIC_PRESETS[0].name}
              onChange={handlePresetChange}
              sx={{ color: "#1E293B", backgroundColor: "#FFFFFF" }}
            >
              {HISTORIC_PRESETS.map((p) => (
                <option key={p.name} value={p.name}>
                  {t(`historicOverlay.preset.${p.id || p.name}`, p.name)}
                </option>
              ))}
              <option value="custom">
                {t("historicOverlay.customUrlOption", "Egendefinert flise-URL (XYZ)")}
              </option>
            </Select>
          </FormControl>

          {isKartverket && (
            <Typography sx={{ fontSize: "11px", color: "#B45309", mt: 0.5, fontStyle: "italic" }}>
              {t(
                "historicOverlay.kartverketWarning",
                "Merk: Kartverkets kart dekker kun Norge og vil vise en kant ved riksgrensen."
              )}
            </Typography>
          )}

          {overlay.name === "custom" && (
            <TextField
              size="small"
              fullWidth
              label="XYZ Tile URL"
              value={overlay.url || ""}
              onChange={handleUrlChange}
              placeholder="https://.../{z}/{x}/{y}.png"
              error={Boolean(overlay.url && !isSafeHistoricTileUrl(overlay.url))}
              helperText={
                overlay.url && !isSafeHistoricTileUrl(overlay.url)
                  ? t(
                      "historicOverlay.invalidUrl",
                      "Ugyldig URL. Kun sikre HTTPS-fliser støttes (https://)."
                    )
                  : ""
              }
            />
          )}

          {waypoints.length > 0 && onUpdateWaypointOpacity && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
              <Typography variant="caption" sx={{ color: "#475569", fontWeight: 700 }}>
                {t("historicOverlay.waypointsOpacity", "Opacitet per etappe (0–100%)")}
              </Typography>
              {waypoints.map((wp, i) => {
                const currentVal =
                  wp.historicMapOpacity !== undefined
                    ? Math.round(Number(wp.historicMapOpacity) * 100)
                    : (i === 0 ? 100 : 0);
                return (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontSize: "12px", width: "80px", color: "#334155" }} noWrap>
                      {wp.title || `${t("historicOverlay.stageLabel", "Etappe")} ${i + 1}`}
                    </Typography>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={currentVal}
                      onChange={(e) => onUpdateWaypointOpacity(i, Number(e.target.value) / 100)}
                      style={{ flex: 1, cursor: "pointer" }}
                      aria-label={`${wp.title || `${t("historicOverlay.stageLabel", "Etappe")} ${i + 1}`} ${t("historicOverlay.opacityLabel", "opacitet")}`}
                    />
                    <Typography sx={{ fontSize: "12px", width: "36px", textAlign: "right", color: "#334155" }}>
                      {currentVal}%
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
